/**
 * Application client side duplication functions
 * Many places on the portal handle lists of items that require functionality to manage duplicate items, 
 * this service contains all client-side duplication functionality for the application
 * @module DuplicatesService
 * @author Bailey Kulman (Fresche Solutions)
 * @date May 2025
 */

let duplicateHandlingFunctions = {
    /**
     * Adds an item to the list and checks for/handles duplicate items.
     * 
     * @param {Array} originalList - The original list to which the item will be added.
     * @param {Object} itemToAdd - The item to be added to the list.
     * @param {string} idField - The field used to identify duplicates.
     * @param {Function} sortingFunction - The function used to sort the list.
     * @param {boolean} isIndependentSortFunction - Indicates if the sorting function is called using the .sort() function (FALSE) or 
     *                                              whether its a stand alone function that returns the sorted list (TRUE).
     * @returns {Array} - The sorted list after adding the item.
     */
    addItemToList(originalList, itemToAdd, idField, sortingFunction, isIndependentSortFunction = false) 
    {
        // Check for existing items
        let existingItems = originalList.filter(item => item[idField] === itemToAdd[idField]);

        if (existingItems.length === 1) {
            existingItems[0].hasDuplicate = true;
            itemToAdd.isDuplicate = true;
        } else if (existingItems.length >= 2) {
            itemToAdd.isDuplicate = true;
        } 

        originalList.push(itemToAdd);

        // Sort list
        if (isIndependentSortFunction) {
            return sortingFunction(originalList);
        } else {
            originalList.sort(sortingFunction);
        }
    },


    /**
     * Removes an item from the list and checks for/handles duplicate items.
     * 
     * @param {Array} originalList - The original list from which the item will be removed.
     * @param {number} itemIndex - The index of the item to be removed.
     * @param {string} idField - The field used to identify duplicates.
     */
    deleteItemFromList(originalList, itemIndex, idField) 
    {
        var originalItem = { ...originalList[itemIndex] };

        originalList.splice(itemIndex, 1);

        // Check if item was a duplicate
        if (originalItem.isDuplicate || originalItem.hasDuplicate) {
            // Get remaining items
            var remainingItems = originalList.filter(item => item[idField] === originalItem[idField]);

            if (remainingItems.length === 1) {
                remainingItems[0].hasDuplicate = false;
                remainingItems[0].isDuplicate = false;
            } else if (remainingItems.length >= 2 && originalItem.hasDuplicate) {
                remainingItems[0].hasDuplicate = true;
                remainingItems[0].isDuplicate = false;
            }
        }
    },


    /**
     * Consolidates duplicate items in the list by updating the first occurrence and removing the rest.
     * 
     * @param {Array} originalList - The original list containing items to be consolidated.
     * @param {Object} updatedItem - The item to be used for consolidation.
     * @param {string} idField - The field used to identify duplicates.
     */
    consolidateItem(originalList, updatedItem, idField) 
    {
        var items = originalList.filter(item => item[idField] === updatedItem[idField]);

        for (let i = 0; i < items.length; i++) {
            var index = originalList.indexOf(items[i]);

            if (i == 0) {
                originalList[index] = updatedItem;
            } else {
                originalList.splice(index, 1);
            }
        }
    },


    /**
     * Sorts list items based on sortingGroup and lineNum.
     * 
     * The sortingGroup is the line number of the first occurrence of the item in the list.
     * This leads to the list being sorted by line number, with duplicate items grouped together.
     * 
     * @param {*} item1 - The first item to compare (item must have itemNumber and lineNum).
     * @param {*} item2 - The second item to compare (item must have itemNumber and lineNum).
     * @returns {number} - Returns -1 if item1 should come before item2, 1 if item1 should come after item2, or 0 if they are equal.
     */
    compareListItems(item1, item2) 
    {
        // First sort by sortingGroup (primary sort field)
        if (item1.sortingGroup < item2.sortingGroup) {
            return -1;
        } else if (item1.sortingGroup > item2.sortingGroup) {
            return 1;
        } else {
            // If lineNum values are equal, then sort by lineNum (secondary sort field)
            if (item1.lineNum < item2.lineNum) {
                return -1;
            } else if (item1.lineNum > item2.lineNum) {
                return 1;
            } else {
                return 0;
            }
        }
    },
};

export { duplicateHandlingFunctions };
